import Konva from 'konva'
import {VIEW_MODES} from '../../constants/editorConfig'
import {KonvaAdapter} from './KonvaAdapter'

export function createKonvaRuntimeAdapter({editorEngine, interaction, createInputAdapter}) {
    let stage = null
    let dragLayer = null
    let adapters = {}
    let unbindInput = null
    let currentViewMode = VIEW_MODES.OVERLAY

    function layerToCanvas(layer, options = {}, exportOptions = {}) {
        const base = {pixelRatio: exportOptions.pixelRatio ?? 1, mimeType: 'image/png'}
        const hasBounds =
            options.x != null && options.y != null && options.width != null && options.height != null

        // Save and reset stage transform so export uses model-space coordinates
        const parentStage = layer.getStage()
        let savedScale, savedPosition
        if (parentStage) {
            savedScale = parentStage.scale()
            savedPosition = parentStage.position()
            parentStage.scale({x: 1, y: 1})
            parentStage.position({x: 0, y: 0})
        }

        const canvas = hasBounds
            ? layer.toCanvas({...base, x: options.x, y: options.y, width: options.width, height: options.height})
            : layer.toCanvas(base)

        if (parentStage) {
            parentStage.scale(savedScale)
            parentStage.position(savedPosition)
        }

        return canvas
    }

    function measureTextNodeRect(nodeId) {
        const overlayAdapter = adapters[VIEW_MODES.OVERLAY]
        if (!overlayAdapter || !stage) return null
        const entry = overlayAdapter.nodes.get(nodeId)
        if (!entry?.node) return null
        const node = entry.node
        const {viewport} = editorEngine.getState()
        const scale = viewport.scale
        const stageX = -viewport.x * scale
        const stageY = -viewport.y * scale
        return {
            x: node.x() * scale + stageX,
            y: node.y() * scale + stageY,
            width: node.width() * scale,
            height: node.height() * scale,
        }
    }

    return {
        attach({stage: s, layers}, runtime) {
            stage = s
            dragLayer = new Konva.Layer()
            stage.add(dragLayer)

            const onStartTextEditing = runtime ? (nodeId) => runtime.startTextEditing(nodeId) : undefined

            adapters = {
                [VIEW_MODES.OVERLAY]: new KonvaAdapter({
                    stage,
                    layer: layers[VIEW_MODES.OVERLAY],
                    dragLayer,
                    editor: editorEngine,
                    interaction,
                    renderProfile: VIEW_MODES.OVERLAY,
                    onStartTextEditing,
                    runtime,
                }),

                [VIEW_MODES.SILHOUETTE]: new KonvaAdapter({
                    stage,
                    layer: layers[VIEW_MODES.SILHOUETTE],
                    editor: editorEngine,
                    interaction,
                    renderProfile: VIEW_MODES.SILHOUETTE,
                    runtime,
                }),
            }

            if (runtime?._setMeasureTextNodeRect) {
                runtime._setMeasureTextNodeRect(measureTextNodeRect)
            }

            unbindInput = createInputAdapter({
                stage,
                editor: editorEngine,
                interaction,
                runtime,
            })
        },

        mount() {
            Object.values(adapters).forEach((a) => a.mount())
            this.setViewMode(currentViewMode)
        },

        unmount() {
            Object.values(adapters).forEach((a) => a.unmount())
            unbindInput?.()
            unbindInput = null
        },

        setViewMode(mode) {
            currentViewMode = mode

            Object.entries(adapters).forEach(([key, adapter]) => {
                adapter.layer.visible(key === mode)
            })
            if (dragLayer) {
                dragLayer.visible(mode === VIEW_MODES.OVERLAY)
            }

            stage?.batchDraw?.()
        },

        getMode() {
            return currentViewMode
        },

        getInteraction() {
            return interaction
        },

        getViewportCenter() {
            if (!stage) return null

            const {viewport} = editorEngine.getState()
            const scale = viewport?.scale || 1
            const stageWidth = stage.width?.() ?? 0
            const stageHeight = stage.height?.() ?? 0

            return {
                x: viewport.x + stageWidth / (2 * scale),
                y: viewport.y + stageHeight / (2 * scale),
                width: stageWidth / scale,
                height: stageHeight / scale,
            }
        },

        getSilhouetteCanvas(options = {}, exportOptions = {}) {
            const layer = adapters[VIEW_MODES.SILHOUETTE]?.layer
            if (!layer || !stage) return null
            const previousMode = currentViewMode
            this.setViewMode(VIEW_MODES.SILHOUETTE)
            layer.visible(true)
            if (layer.batchDraw) layer.batchDraw()
            if (stage.batchDraw) stage.batchDraw()

            const canvas = layerToCanvas(layer, options, exportOptions)
            this.setViewMode(previousMode)
            if (stage.batchDraw) stage.batchDraw()
            return canvas
        },

        getOverlayCanvas(options = {}, exportOptions = {}) {
            const layer = adapters[VIEW_MODES.OVERLAY]?.layer
            if (!layer || !stage) return null

            const transformers = layer.find ? layer.find('Transformer') : []
            const previousVisible = transformers.map((tr) => tr.visible())
            transformers.forEach((tr) => tr.visible(false))
            if (transformers.length > 0 && layer.batchDraw) layer.batchDraw()
            if (stage.batchDraw) stage.batchDraw()

            const canvas = layerToCanvas(layer, options, exportOptions)

            transformers.forEach((tr, i) => tr.visible(previousVisible[i]))
            if (transformers.length > 0 && layer.batchDraw) layer.batchDraw()
            if (stage.batchDraw) stage.batchDraw()

            return canvas
        },
    }
}

